class FreeGiftsBlock extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#bindVariantSelectors();
    this.#bindGiftToggles();
    this.#bindProductUpdates();
    this.#updateEligibility();
    this.#updateAddToCartText();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #bindVariantSelectors() {
    const signal = this.#abortController?.signal;

    this.querySelectorAll('[data-free-gift-swatch]').forEach((swatch) => {
      swatch.addEventListener('click', (event) => this.#handleSwatchClick(event), { signal });
    });
  }

  /** @param {Event} event */
  #handleSwatchClick(event) {
    const swatch = event.currentTarget;
    if (!(swatch instanceof HTMLElement)) return;

    const item = swatch.closest('[data-free-gift-item]');
    const variantInput = item?.querySelector('[data-free-gift-variant-select]');
    const selectedLabel = item?.querySelector('[data-free-gift-selected-variant]');
    const image = item?.querySelector('[data-free-gift-image]');
    const imageWrap = item?.querySelector('[data-free-gift-image-wrap]');

    if (variantInput instanceof HTMLInputElement && swatch.dataset.variantId) {
      variantInput.value = swatch.dataset.variantId;
    }

    if (selectedLabel && swatch.dataset.variantTitle) {
      selectedLabel.textContent = swatch.dataset.variantTitle;
    }

    if (image instanceof HTMLImageElement && swatch.dataset.image) {
      image.removeAttribute('srcset');
      image.src = swatch.dataset.image;
    }

    if (imageWrap instanceof HTMLElement && swatch.dataset.imageRatio) {
      imageWrap.style.setProperty('--free-gift-image-ratio', swatch.dataset.imageRatio);
    }

    item?.querySelectorAll('[data-free-gift-swatch]').forEach((button) => {
      const isSelected = button === swatch;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  #bindGiftToggles() {
    const signal = this.#abortController?.signal;

    this.querySelectorAll('[data-free-gift-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (event) => this.#handleGiftToggleClick(event), { signal });
      toggle.addEventListener('keydown', (event) => this.#handleGiftToggleKeydown(event), { signal });
      this.#syncGiftToggle(toggle);
    });
  }

  /** @param {Event} event */
  #handleGiftToggleClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('select, option, input, button, a')) return;

    const toggle = event.currentTarget;
    if (toggle instanceof HTMLElement) {
      this.#toggleGift(toggle);
    }
  }

  /** @param {Event} event */
  #handleGiftToggleKeydown(event) {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== ' ' && event.key !== 'Enter') return;

    event.preventDefault();
    const toggle = event.currentTarget;
    if (toggle instanceof HTMLElement) {
      this.#toggleGift(toggle);
    }
  }

  /** @param {HTMLElement} toggle */
  #toggleGift(toggle) {
    const checkbox = toggle.querySelector('[data-free-gift-checkbox]');
    if (!(checkbox instanceof HTMLInputElement) || checkbox.disabled) return;

    checkbox.checked = !checkbox.checked;
    this.#syncGiftToggle(toggle);
    this.#updateAddToCartText();
  }

  /** @param {Element} toggle */
  #syncGiftToggle(toggle) {
    const checkbox = toggle.querySelector('[data-free-gift-checkbox]');
    const state = toggle.querySelector('[data-free-gift-state]');
    if (!(toggle instanceof HTMLElement) || !(checkbox instanceof HTMLInputElement)) return;

    toggle.setAttribute('aria-checked', checkbox.checked ? 'true' : 'false');
    if (state) {
      state.textContent = checkbox.checked ? this.#selectedLabel : this.#unselectedLabel;
    }
  }

  #bindProductUpdates() {
    const signal = this.#abortController?.signal;
    const section = this.closest('.shopify-section');
    const form = this.#productForm;

    section?.addEventListener('variant:update', (event) => {
      const variantEvent = /** @type {CustomEvent} */ (event);
      const price = variantEvent.detail?.resource?.price;
      if (typeof price === 'number') {
        this.dataset.productPrice = price.toString();
      }

      this.#updateEligibility();
      requestAnimationFrame(() => this.#updateAddToCartText());
    }, { signal });

    form?.addEventListener('quantity-selector:update', () => {
      this.#updateEligibility();
      this.#updateAddToCartText();
    }, { signal });
    form?.addEventListener('change', () => {
      this.#updateEligibility();
      this.#updateAddToCartText();
    }, { signal });
    form?.addEventListener('input', () => {
      this.#updateEligibility();
      this.#updateAddToCartText();
    }, { signal });

  }

  #updateEligibility() {
    const subtotal = Number(this.dataset.cartSubtotal) || 0;
    const productPrice = Number(this.dataset.productPrice) || 0;
    const quantity = this.#quantity;
    const projectedSubtotal = subtotal + productPrice * quantity;
    const giftItems = this.querySelectorAll('[data-free-gift-item]');
    let visibleGiftCount = 0;
    giftItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const qualificationType = item.dataset.qualificationType;
      let qualifies = false;

      if (qualificationType === 'quantity') {
        const minimumQuantity = Number(item.dataset.minQuantity) || 0;
        const cartQualifyingQuantity = Number(item.dataset.cartQualifyingQuantity) || 0;
        const productQualifies = item.dataset.productQualifies === 'true';
        qualifies = productQualifies && cartQualifyingQuantity + quantity >= minimumQuantity;
      } else if (qualificationType === 'spend') {
        const threshold = Number(item.dataset.threshold) || 0;
        qualifies = threshold > 0 && projectedSubtotal >= threshold;
      }

      const wasEligible = item.dataset.eligible === 'true';
      const checkbox = item.querySelector('[data-free-gift-checkbox]');
      const selector = item.querySelector('[data-free-gift-variant-select]');

      item.hidden = !qualifies;
      item.dataset.eligible = qualifies ? 'true' : 'false';
      if (checkbox instanceof HTMLInputElement) {
        checkbox.disabled = !qualifies;
        if (!qualifies) {
          checkbox.checked = false;
        } else if (!wasEligible) {
          checkbox.checked = true;
        }
      }
      if (selector instanceof HTMLInputElement) {
        selector.disabled = !qualifies;
      }
      item.querySelectorAll('[data-free-gift-swatch]').forEach((swatch) => {
        if (swatch instanceof HTMLButtonElement) {
          swatch.disabled = !qualifies;
        }
      });

      if (qualifies) {
        visibleGiftCount += 1;
      }

      this.#syncGiftToggle(item);
    });

    const giftText = this.querySelector('[data-free-gifts-text]');
    if (giftText instanceof HTMLElement) {
      giftText.textContent =
        visibleGiftCount > 1 ? giftText.dataset.pluralText || '' : giftText.dataset.singularText || '';
    }

    this.hidden = giftItems.length > 0 ? visibleGiftCount === 0 : this.dataset.designMode !== 'true';
    this.#updateAddToCartText();
  }

  #updateAddToCartText() {
    const textTarget = this.#addToCartTextTarget;
    if (!textTarget) return;

    const suffix = ' + Free Gift';
    const baseText = textTarget.dataset.freeGiftBaseText || textTarget.textContent?.replace(suffix, '').trim() || '';
    if (!textTarget.dataset.freeGiftBaseText) {
      textTarget.dataset.freeGiftBaseText = baseText;
    }

    textTarget.textContent = this.#hasSelectedGift ? `${baseText}${suffix}` : baseText;
  }

  /** @returns {HTMLElement | null} */
  get #productForm() {
    const formId = this.dataset.productFormId;
    if (!formId) return null;

    return document.getElementById(formId);
  }

  /** @returns {number} */
  get #quantity() {
    const form = this.#productForm;
    const quantityInput = form?.querySelector('[name="quantity"]');

    if (quantityInput instanceof HTMLInputElement) {
      return Number(quantityInput.value) || 1;
    }

    return 1;
  }

  /** @returns {HTMLElement | null} */
  get #addToCartTextTarget() {
    const form = this.#productForm;
    return form?.querySelector('.add-to-cart-text__content > span > span') || null;
  }

  /** @returns {boolean} */
  get #hasSelectedGift() {
    return Array.from(this.querySelectorAll('[data-free-gift-checkbox]')).some((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement)) return false;
      const item = checkbox.closest('[data-free-gift-item]');
      return checkbox.checked && !checkbox.disabled && item instanceof HTMLElement && !item.hidden;
    });
  }

  /** @returns {string} */
  get #selectedLabel() {
    return this.dataset.selectedLabel || 'Included';
  }

  /** @returns {string} */
  get #unselectedLabel() {
    return this.dataset.unselectedLabel || 'Add gift';
  }
}

if (!customElements.get('free-gifts-block')) {
  customElements.define('free-gifts-block', FreeGiftsBlock);
}
